import { z } from "zod";
import type { FinancialYear } from "../types.js";

/**
 * income-tests — the shared "adjusted income / add-backs" engine.
 *
 * Australian means-tests each derive a DIFFERENT income figure from the same
 * underlying components. This module encodes the four definitions that the
 * AU-08 (MLS), Div 293, HELP/STSL, co-contribution, second-earner and CCS/FTB
 * surfaces all need, so none of them re-implement the add-back rules:
 *
 *   1. Income for surcharge purposes   (MLS, private health rebate, Div 293 base)
 *   2. HELP/STSL repayment income
 *   3. Division 293 income
 *   4. Adjusted taxable income (ATI)   (family assistance: CCS, FTB, …)
 *
 * The load-bearing subtlety is the reportable fringe benefits amount (RFBA):
 *   - MLS / Div 293 / HELP use the FULL grossed-up RFBA.
 *   - Family-assistance ATI un-grosses-up ONLY the s57A exempt-employer portion
 *     via (1 − FBT rate); a non-exempt (ordinary novated-lease) benefit counts
 *     at FULL grossed-up value even for ATI.
 *
 * The factor is (1 − 0.47) = 0.53 for FBT years to 31 Mar 2027 — NOT 0.535.
 *
 * Pure functions only: inputs -> rules -> outputs. No I/O, no Date.now(); the
 * assessment year (FBT-year-bearing FY) is always passed in.
 */

import type { RuleSetMeta } from "../types.js";

/**
 * RFBA split by employer FBT class. The grossed-up reportable amount is the
 * SAME number both employer classes report (type-2 gross-up, ~1.8868) — the
 * classes differ only in how the family-assistance ATI treats them.
 *
 *   - `exemptEmployer`     — s57A exempt employers: PBIs, health-promotion
 *     charities, public/NFP hospitals, ambulance. ATI applies (1 − FBT rate).
 *   - `nonExemptEmployer`  — ordinary employers (the typical novated lease).
 *     ATI counts this at FULL grossed-up value, same as MLS/Div293/HELP.
 *
 * Both are the grossed-up REPORTED amounts (what appears at IT1), each ≥ 0.
 */
export const RfbaSchema = z
  .object({
    /** Grossed-up RFBA from a s57A FBT-exempt employer (hospital/PBI/NFP). */
    exemptEmployer: z.number().min(0).default(0),
    /** Grossed-up RFBA from a non-exempt (ordinary) employer — full value in ATI. */
    nonExemptEmployer: z.number().min(0).default(0),
  })
  .default({ exemptEmployer: 0, nonExemptEmployer: 0 });

export type Rfba = z.infer<typeof RfbaSchema>;

/**
 * One canonical set of income components. Each means-test derives its figure by
 * selecting and combining these — that's the whole point of the module: collect
 * once, derive many. All amounts in whole/decimal AUD, signs as noted.
 */
export const IncomeComponentsSchema = z.object({
  /** Assessable income minus deductions — your taxable income (≥ 0). */
  taxableIncome: z.number().min(0),

  /**
   * Reportable fringe benefits, split by employer FBT class. The grossed-up
   * reported amounts (IT1). Default: no reportable benefits.
   */
  reportableFringeBenefits: RfbaSchema,

  /**
   * Reportable (concessional) super contributions — salary-sacrificed +
   * personal deductible. Add-back for MLS/Div293/HELP/ATI.
   */
  reportableSuperContributions: z.number().min(0).default(0),

  /** Net financial-investment loss (≥ 0; it's a loss magnitude added back). */
  netFinancialInvestmentLoss: z.number().min(0).default(0),

  /** Net rental-property loss (≥ 0; loss magnitude added back). */
  netRentalPropertyLoss: z.number().min(0).default(0),

  /**
   * Net family-trust-distribution-tax amount. Part of income for surcharge
   * purposes (ATO) — the FTDT amount, not a general trust distribution.
   */
  familyTrustDistributionTax: z.number().min(0).default(0),

  /** Exempt foreign employment income (s23AF / s23AG) — add-back for HELP. */
  exemptForeignEmploymentIncome: z.number().min(0).default(0),

  /** Tax-free pensions and benefits — ATI add-back (family assistance). */
  taxFreePensionsBenefits: z.number().min(0).default(0),

  /** Target foreign income — ATI add-back (family assistance). */
  targetForeignIncome: z.number().min(0).default(0),

  /** Child maintenance expenditure — SUBTRACTED from ATI (≥ 0). */
  childMaintenanceExpenditure: z.number().min(0).default(0),

  /**
   * Taxed elements of a super lump sum / FHSS released amount that the person
   * received and that are SUBTRACTED in the Division 293 income calculation
   * (sign matters: Div293 income removes these from low-tax/taxable amounts).
   * (≥ 0; subtracted.)
   */
  superLumpSumTaxedElementsAboveCap: z.number().min(0).default(0),
});

export type IncomeComponents = z.infer<typeof IncomeComponentsSchema>;

/** Total net investment loss = net financial-investment + net rental loss. */
export function netInvestmentLoss(c: IncomeComponents): number {
  return c.netFinancialInvestmentLoss + c.netRentalPropertyLoss;
}

/** The full grossed-up RFBA (both employer classes) — used by MLS/Div293/HELP. */
export function grossedUpRfba(r: Rfba): number {
  return r.exemptEmployer + r.nonExemptEmployer;
}

/**
 * A versioned income-tests ruleset. The only FY-varying figure is the FBT rate
 * (hence the family-assistance un-gross-up factor) and the RFBA reporting
 * threshold; thresholds for specific tests (MLS bands, Div293 cap) live with
 * their own product rulesets and are NOT duplicated here.
 */
export interface IncomeTestsRuleSet extends Omit<RuleSetMeta, "product"> {
  /** Product tag is shared infra, not a single AU0x product. */
  product: "SHARED";
  /** FBT rate in force for this FY's FBT year (fraction, e.g. 0.47 = 47%). */
  fbtRate: number;
  /**
   * Family-assistance un-gross-up factor applied to the EXEMPT-employer RFBA
   * portion = (1 − fbtRate). Stored explicitly so a test can assert it equals
   * 0.53 and is not the stale 0.535.
   */
  exemptEmployerAtiFactor: number;
  /**
   * RFBA reporting threshold: an RFBA is reported where the TAXABLE value of
   * benefits exceeds this in the FBT year (1 Apr – 31 Mar). The reported amount
   * is then the grossed-up figure. (The $2,000 trigger is on the taxable value.)
   */
  rfbaReportingThreshold: number;
  /**
   * Grossed-up FBT-exempt packaging caps, per employee per FBT year. Held for
   * reference / downstream packaging tools — not applied to the income derivations.
   */
  fbtExemptCapsGrossedUp: {
    /** PBIs and health-promotion charities. */
    pbiHpc: number;
    /** Public and NFP hospitals, ambulance. */
    hospitalAmbulance: number;
    /** Separate meal-entertainment / venue-hire grossed-up cap. */
    mealEntertainment: number;
  };
}

/** Which income definition to derive. */
export type IncomeTestKind =
  | "surcharge" // income for surcharge purposes (MLS, rebate, Div293 base)
  | "help" // HELP/STSL repayment income
  | "div293" // Division 293 income
  | "ati"; // adjusted taxable income (CCS/FTB)

export const INCOME_TEST_KINDS = [
  "surcharge",
  "help",
  "div293",
  "ati",
] as const;

/** Result of deriving every income definition from one component set. */
export interface IncomeTestsOutput {
  /** Income for surcharge purposes (MLS / private-health rebate / Div293 base). */
  incomeForSurchargePurposes: number;
  /** HELP/STSL repayment income. */
  helpRepaymentIncome: number;
  /** Division 293 income. */
  div293Income: number;
  /** Adjusted taxable income (family assistance: CCS/FTB). */
  adjustedTaxableIncome: number;
  /**
   * The adjusted fringe benefits total used inside ATI:
   *   exemptEmployer × (1 − FBT rate) + nonExemptEmployer.
   * Exposed so surfaces can show the un-gross-up working.
   */
  adjustedFringeBenefitsTotal: number;
  /** The full grossed-up RFBA used by the surcharge/HELP/Div293 derivations. */
  grossedUpFringeBenefits: number;
}

export interface IncomeTestsInput {
  components: IncomeComponents;
  /**
   * The assessment financial year, e.g. "2025-26" — passed in (never derived
   * from the clock) so historical re-runs resolve against the FBT rate in force.
   */
  assessmentYear: FinancialYear;
}
